import { useEffect, useState } from 'react';
import { useToast } from '../../hooks/useToast';
import { Text } from '../Text/Text';
import { Flex } from '../Flex/Flex';
import { View } from '../View/View';
import { useIsMounted, useTrellisFocusRing } from '../../hooks';
import { getVariantStyles } from './Toast.helpers';
import styled from 'styled-components';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledButton = styled.button.withConfig({
  displayName: "Toast__StyledButton",
  componentId: "sc-1yxvcmd-0"
})(({
  $focusRingStyles
}) => {
  return Object.assign({
    backgroundColor: 'transparent',
    border: 'none',
    color: '#7C98B6'
  }, $focusRingStyles);
});
const ToastCloseButton = ({
  onPress
}) => {
  const {
    focusProps,
    focusRingStyles
  } = useTrellisFocusRing({});
  return /*#__PURE__*/_jsx(StyledButton, Object.assign({
    onClick: onPress,
    $focusRingStyles: focusRingStyles
  }, focusProps, {
    children: /*#__PURE__*/_jsx("svg", {
      viewBox: "0 0 14 14",
      xmlns: "http://www.w3.org/2000/svg",
      fill: "currentColor",
      width: '10px',
      height: '10px',
      stroke: "currentColor",
      strokeWidth: 1.5,
      children: /*#__PURE__*/_jsx("path", {
        d: "M14.5,1.5l-13,13m0-13,13,13",
        transform: "translate(-1 -1)"
      })
    })
  }));
};

/**
 * TODO:
 * - replace close button with CloseButton component once it's been built
 */
const Toast = ({
  id,
  message,
  variant = 'info',
  autoClose
}) => {
  const isMounted = useIsMounted();
  const {
    toast
  } = useToast();

  // State keeping track of whether the toast is in the process of being removed
  const [isRemoving, setIsRemoving] = useState(false);
  const [timerId, setTimerId] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [remainingTime, setRemainingTime] = useState(3000);
  useEffect(() => {
    if (!autoClose) return;
    const savedTimerId = setTimeout(() => {
      setIsRemoving(true);
    }, 3000);
    setTimerId(savedTimerId);
    setStartTime(Date.now());
    return () => {
      clearTimeout(savedTimerId);
    };
  }, [autoClose]);
  useEffect(() => {
    if (autoClose && isRemoving) {
      toast.remove(id);
      setIsRemoving(false);
      setTimerId(null);
      setStartTime(null);
      setRemainingTime(0);
    }
  }, [isRemoving, id, toast, autoClose]);
  const handleRemove = () => {
    toast.remove(id);
  };

  // Pause the timer when the mouse enters the toast
  const handleMouseEnter = () => {
    if (autoClose && startTime && timerId !== null) {
      setRemainingTime(prevRemainingTime => prevRemainingTime - (Date.now() - startTime));
      clearTimeout(timerId);
      setTimerId(null);
    }
  };

  // Resume the timer when the mouse leaves the toast
  const handleMouseLeave = () => {
    if (autoClose && remainingTime && timerId === null) {
      const savedTimerId = setTimeout(() => {
        if (isMounted()) {
          setIsRemoving(true);
        }
      }, remainingTime);
      setTimerId(savedTimerId);
      setStartTime(Date.now());
    }
  };
  return /*#__PURE__*/_jsx(View, Object.assign({}, getVariantStyles(variant), {
    role: "status",
    display: "inline-block",
    paddingBlock: "500",
    paddingInline: "400",
    boxShadow: "alert-floating",
    borderBlockWidth: "alert-default",
    borderInlineWidth: "alert-default",
    borderBlockStyle: "solid",
    borderInlineStyle: "solid",
    overflow: "hidden",
    inlineSize: "350px",
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    children: /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "row",
      alignItems: "center",
      children: [/*#__PURE__*/_jsx(Flex, {
        flexGrow: 1,
        children: /*#__PURE__*/_jsx(Text, {
          children: message
        })
      }), /*#__PURE__*/_jsx(ToastCloseButton, {
        onPress: handleRemove
      })]
    })
  }));
};
export default Toast;